import { Component, OnDestroy, OnInit } from '@angular/core';
import { PatientService } from '../patient.service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { BookingService } from '../../services/booking.service';
import { SessionService } from '../../services/session-service.service';
import { format, parseISO } from 'date-fns';
import { BehaviorSubject, Subject, Subscription, throwError } from 'rxjs';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { ImageEditingDialogComponent } from '../../components/shared/image-editing-dialog/image-editing-dialog.component';
import { HttpClient } from '@angular/common/http';
import { RemovePetDialogComponent } from '../remove-pet-dialog/remove-pet-dialog.component';
import { environment } from '../../../environments/environment';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { catchError } from 'rxjs/operators';
import { AppointmentType } from '@appyvet/vetbooker-definitions/dist/appointment_type';
import {
  ClientPatientDetails,
  PetDeactivationReason,
} from '@appyvet/vetbooker-definitions/dist/client_patient_details';
import {
  BreedItem,
  PatientExtraDetailsResponse,
  VbExtendedPatient,
} from '@appyvet/vetbooker-definitions/dist/patient';
import { Resource } from '@appyvet/vetbooker-definitions/dist/resource';
import {
  FirstAvailableSlot,
  PurePatient,
} from '@appyvet/vetbooker-definitions/dist/appointments';
import { utcToZonedTime } from 'date-fns-tz';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import {
  NgxFileDropEntry,
  FileSystemFileEntry,
  FileSystemDirectoryEntry,
} from 'ngx-file-drop';
import { AlertDialogComponent } from 'src/app/components/shared/alert-dialog/alert-dialog.component';
import { th } from 'date-fns/locale';

@Component({
  selector: 'app-patient-detail',
  templateUrl: './patient-detail.component.html',
  styleUrls: ['./patient-detail.component.scss'],
})
export class PatientDetailComponent implements OnInit, OnDestroy {
  patient: VbExtendedPatient;
  patientUpdate: boolean;
  canViewHistory: boolean;
  vaccineHistoryEnabled: boolean;
  resource: Resource;
  apptType: AppointmentType;
  slot: FirstAvailableSlot;
  imageLoading$ = new BehaviorSubject<boolean>(false);
  customPrescriptions: boolean;
  allowPatientImages: boolean;
  hideReminders: boolean;
  hideInsurance: boolean;
  hideCarePlans: boolean;
  canDeactivatePatient: boolean;
  isVets4Pets = environment.VETS_4_PETS;
  bioLoading$ = new Subject<boolean>();
  deactivateLoading$ = new Subject<boolean>();
  showColor = !environment.VETS_4_PETS && !environment.GROOM;
  purePatientItem: PurePatient;
  disableQuickBook: boolean;
  isGroomRoom = environment.GROOM;
  public files: NgxFileDropEntry[] = [];
  // options: InputFileOptions = {
  //   multiple: false,
  //   accept: [MineTypeEnum.Image_Png, MineTypeEnum.Image_Jpeg]
  // };
  private uploadSub: Subscription;
  private clientDetailsSub: Subscription;
  private paramsSub: Subscription;
  private patientsSub: Subscription;
  private clinicName: string;
  private timezone: string;
  canBook: boolean;
  breeds: BreedItem[] = [];
  routeDataSub: Subscription;

  constructor(
    public patientService: PatientService,
    private route: ActivatedRoute,
    private bookingService: BookingService,
    private sessionService: SessionService,
    private matDialog: MatDialog,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private gaService: GoogleAnalyticsService,
    private router: Router,
  ) {}

  public dropped(files: NgxFileDropEntry[]) {
    this.files = files;
    for (const droppedFile of files) {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          const dialogRef = this.matDialog.open(ImageEditingDialogComponent, {
            data: { file: file, roundCrop: !this.isVets4Pets },
          });
          dialogRef.afterClosed().subscribe((base64Image: string) => {
            this.uploadImage(base64Image);
          });
        });
      }
    }
  }

  public fileOver(event) {
    console.log(event);
  }

  public fileLeave(event) {
    console.log(event);
  }

  ngOnInit(): void {
    this.paramsSub = this.route.paramMap.subscribe((params: ParamMap) => {
      // Ensure only one subscription to the upload service even if navigating between patients
      this.uploadSub?.unsubscribe();
      this.patient = this.patientService.getPatient(
        params.get('patientNumber'),
      );
    });

    this.routeDataSub = this.route.data.subscribe(
      (data: { clientPatientDetails: ClientPatientDetails }) => {
        this.breeds = data.clientPatientDetails.breeds;
      },
    );

    this.clientDetailsSub = this.sessionService.clientPatientDetails$.subscribe(
      (clientPatientDetails) => {
        this.clinicName = clientPatientDetails.themeSettings.customName;
        this.timezone = clientPatientDetails.timezone;
        this.disableQuickBook =
          clientPatientDetails.appointmentSettings.quickBookDisabled;
        this.hideReminders =
          clientPatientDetails.patientViewSettings.hideReminders;
        this.hideInsurance =
          clientPatientDetails.patientViewSettings.hideInsurance;
        this.hideCarePlans =
          clientPatientDetails.patientViewSettings.hideHealthPlans;
        this.allowPatientImages =
          clientPatientDetails.editSettings.allowPatientImages;
        this.customPrescriptions =
          clientPatientDetails.prescriptions.enableCustomPrescriptions;
        this.canDeactivatePatient = clientPatientDetails.canDeactivatePatient;
        this.canBook = clientPatientDetails.acceptingBookings;
        if (!this.disableQuickBook && clientPatientDetails.acceptingBookings) {
          this.bookingService
            .getFirstAvailableAppointment$({
              isVaccine: false,
              numberOfResults: 1,
            })
            .subscribe((firstAppt) => {
              if (firstAppt) {
                this.slot = firstAppt.slots[0];
                this.purePatientItem =
                  this.bookingService.convertExtendPatientToPurePatient(
                    this.patient,
                  );
                this.slot.selectedPatient = this.purePatientItem;
                this.resource = clientPatientDetails.resources.find(
                  (resource) => resource.objectId === firstAppt.resource,
                );
                this.apptType = clientPatientDetails.appointmentTypes.find(
                  (apptType) => apptType.objectId === firstAppt.apptType,
                );
              }
            });
        }
      },
    );
    this.canViewHistory = this.patientService.canViewHistory;
    this.vaccineHistoryEnabled = this.patientService.vaccineHistoryEnabled;
    this.patientsSub = this.patientService.patients$.subscribe((patients) => {
      if (patients) {
        this.patient = this.patientService.getPatient(
          this.patient.patientNumber,
        );
      }
    });
  }

  ngOnDestroy() {
    this.patientsSub?.unsubscribe();
    this.clientDetailsSub?.unsubscribe();
    this.uploadSub?.unsubscribe();
    this.paramsSub?.unsubscribe();
    this.routeDataSub?.unsubscribe();
  }

  getDateOfBirth() {
    if (this.patient.dateOfBirth) {
      return format(parseISO(this.patient.dateOfBirth), 'dd/MM/yyyy');
    } else {
      return 'Unknown';
    }
  }

  getLastVisitDate() {
    if (this.patient.extraInfo && this.patient.extraInfo.lastVisitDate) {
      return format(
        utcToZonedTime(this.patient.extraInfo.lastVisitDate, this.timezone),
        'dd/MM/yyyy HH:mm',
      );
    } else {
      return 'No previous visits found';
    }
  }

  uploadImage(image: string) {
    this.gaService.event('Add Photo', 'Pet Details', this.clinicName);
    this.imageLoading$.next(true);
    if (image) {
      if (environment.VETS_4_PETS) {
        this.patientService
          .uploadV4PPhoto(image, this.patient.patientNumber)
          .then(
            (result) => {
              this.imageLoading$.next(false);
              let extraInfo = this.patient.extraInfo;
              if (!extraInfo) {
                extraInfo = {} as PatientExtraDetailsResponse;
              }
              extraInfo.photoBase64 = result;
              this.patient.extraInfo = extraInfo;
            },
            () => {
              this.imageLoading$.next(false);
            },
          );
      } else {
        this.patientService
          .uploadPhoto$(image, this.patient.patientNumber)
          .subscribe(
            (result) => {
              this.patient.photo = result;
              this.imageLoading$.next(false);
            },
            (error) => {
              console.log(error);
              this.imageLoading$.next(false);
            },
          );
      }
    } else {
      this.imageLoading$.next(false);
    }
  }

  removePet() {
    this.gaService.event('Remove Pet', 'Pet Details', this.clinicName);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      patient: this.patient,
      reasons: this.patientService.petDeactivationReasons,
    };
    dialogConfig.panelClass = 'no-padding-dialog';
    const dialogRef = this.matDialog.open(
      RemovePetDialogComponent,
      dialogConfig,
    );
    dialogRef.afterClosed().subscribe((reason: PetDeactivationReason) => {
      if (reason) {
        this.deactivateLoading$.next(true);
        this.patientService.deactivatePet(this.patient, reason);
        this.patientService.deactivatedPatientsEvent$
          .pipe(
            catchError((e) => {
              this.deactivateLoading$.next(false);
              this.snackBar.open(
                'Error removing your pet, please contact the clinic',
                null,
                { duration: 15000 },
              );
              return throwError(e);
            }),
          )
          .subscribe(() => {
            this.deactivateLoading$.next(false);
            this.snackBar.open(
              'Successfully requested pet deactivation',
              null,
              { duration: 5000 },
            );
          });
      }
    });
  }

  editPhoto() {
    this.gaService.event('Edit Photo', 'Pet Details', this.clinicName);
    let dialogRef: MatDialogRef<ImageEditingDialogComponent>;
    if (this.patient.extraInfo.photoBase64) {
      dialogRef = this.matDialog.open(ImageEditingDialogComponent, {
        data: {
          base64: 'data:image/png;base64,' + this.patient.extraInfo.photoBase64,
          roundCrop: !this.isVets4Pets,
        },
      });
      dialogRef.afterClosed().subscribe((base64Image: string) => {
        this.uploadImage(base64Image);
      });
    } else {
      this.http
        .get(this.patient.photo.url(), { responseType: 'blob' })
        .subscribe((result) => {
          dialogRef = this.matDialog.open(ImageEditingDialogComponent, {
            data: { file: result, roundCrop: !this.isVets4Pets },
          });
          dialogRef.afterClosed().subscribe((base64Image: string) => {
            this.uploadImage(base64Image);
          });
        });
    }
  }

  deletePhoto() {
    this.gaService.event('Remove Photo', 'Pet Details', this.clinicName);
    this.patientService.deletePhoto(this.patient.patientNumber).then(
      () => {
        if (environment.VETS_4_PETS) {
          this.patient.extraInfo.photoBase64 = null;
        }
        this.patient.photo = null;
      },
      (error) => {
        console.log(error);
      },
    );
  }

  async updateBio(bio: string) {
    this.bioLoading$.next(true);
    await this.patientService.updateBio(this.patient.patientNumber, bio);
    this.patient.extraInfo.petBio = bio;
    this.bioLoading$.next(false);
  }

  editPet() {
    if (this.patient.breedItem.id && this.breeds.length > 0) {
      const matchedBreed = this.breeds.find(
        (breed) => breed.id === this.patient.breedItem.id,
      );

      if (!matchedBreed) {
        const invalidBreedText = `Looks like we can't edit the details of this pet right now. Please contact your veterinary practice to update your pet's breed.`;
        const alertDialogConfig = new MatDialogConfig();
        alertDialogConfig.data = {
          confirmationText: 'Got it!',
          title: 'Customer Notice',
          message: invalidBreedText,
        };
        this.matDialog.open(AlertDialogComponent, alertDialogConfig);
        return;
      }
    }

    this.gaService.event('Update Pet Details', 'Pet Details', this.clinicName);

    this.router.navigateByUrl(
      'pets/' +
        this.patient.patientNumber +
        '/edit?patientNumber=' +
        this.patient.patientNumber,
    );
  }

  bookApptLink() {
    this.gaService.event(
      'Book Appointment From Patient Page',
      'Pet Details',
      this.clinicName,
    );
  }

  orderProductLink() {
    this.gaService.event(
      'Request Prescription From Patient Page',
      'Pet Details',
      this.clinicName,
    );
  }
}
